import type { RefObject } from 'react';

import type { LineChartHandle } from '~/shared/components/LineChart/LineChart';
import type { GraphResponse, OptLogVolumeActions } from '~/shared/types/api';
import type { SelectOption } from '~/shared/utils/types';
import {
  downloadFile,
  generateExportFilename,
  graphDataPointToExportRowCsv,
  graphDataPointToExportRowJson,
  injectDateParams,
  optimizationActionToExportRowCsv,
  optimizationActionToExportRowJson,
  toCsv,
} from '~/shared/utils/exportUtils';

export interface PreRenderedGraphPng {
  name: 'Utilization_Chart' | 'Usage_Chart';
  blob: Blob;
}

export interface ReportsExportParams {
  activeFilters: SelectOption[];
  filterSelection: 'with_filters' | 'all_data';
  showFullGraph: boolean;
  dataType: { graphs: boolean; table: boolean };
  graphFormats: Array<'png' | 'csv' | 'json'>;
  tableFormats: Array<'csv' | 'json'>;
  selectedStartDate: string;
  selectedEndDate: string;
  tableQueryUrl: string;
  tableCountUrl: string;
  chartQueryUrl: string;
  preRenderedGraphPngs?: PreRenderedGraphPng[];
  utilizationChartRef: RefObject<LineChartHandle | null>;
  usageChartRef: RefObject<LineChartHandle | null>;
}

interface ReportsExportDeps {
  fetchData: (
    dataUrl: string,
    countUrl: string,
    initialOffset?: number,
    responseKey?: string
  ) => Promise<unknown[]>;
  xfetch: <T = unknown>(url: string) => Promise<T>;
  activeTenantName: string | null | undefined;
}

export async function executeReportsExport(
  {
    activeFilters,
    filterSelection,
    showFullGraph,
    dataType,
    graphFormats,
    tableFormats,
    selectedStartDate,
    selectedEndDate,
    tableQueryUrl,
    tableCountUrl,
    chartQueryUrl,
    preRenderedGraphPngs,
    utilizationChartRef,
    usageChartRef,
  }: ReportsExportParams,
  { fetchData, xfetch, activeTenantName }: ReportsExportDeps
): Promise<void> {
  const withFilters = filterSelection === 'with_filters';
  const filtersForName = withFilters ? activeFilters : [];
  const today = new Date();

  if (dataType.graphs) {
    const exportPng = graphFormats.includes('png');
    const exportCsv = graphFormats.includes('csv');
    const exportJson = graphFormats.includes('json');

    const chartConfigs = [
      { ref: utilizationChartRef, name: 'Utilization_Chart' },
      { ref: usageChartRef, name: 'Usage_Chart' },
    ] as const;

    if (exportPng && preRenderedGraphPngs?.length) {
      for (const { name, blob } of preRenderedGraphPngs) {
        const filename = generateExportFilename(activeTenantName || 'Account', name, filtersForName, today);
        downloadFile(blob, `${filename}.png`, 'image/png');
      }
    }

    const shouldFetchGraphData = exportCsv || exportJson || (exportPng && !preRenderedGraphPngs?.length);
    if (shouldFetchGraphData) {
      const exportChartUrl = injectDateParams(chartQueryUrl, selectedStartDate, selectedEndDate);
      const graphResponse = await xfetch<GraphResponse>(exportChartUrl);
      const graphData = graphResponse.utilizationGraph ?? [];

      for (const { ref, name } of chartConfigs) {
        const filename = generateExportFilename(activeTenantName || 'Account', name, filtersForName, today);

        if (exportPng && !preRenderedGraphPngs?.length) {
          const blob = await ref.current?.exportWithData(graphData);
          if (!blob) throw new Error('Could not export chart image.');
          downloadFile(blob, `${filename}.png`, 'image/png');
        }

        if (exportCsv) {
          const rows = graphData.map((item) => graphDataPointToExportRowCsv(item, showFullGraph));
          downloadFile(toCsv(rows), `${filename}.csv`, 'text/csv;charset=utf-8;');
        }

        if (exportJson) {
          const rows = graphData.map((item) => graphDataPointToExportRowJson(item, showFullGraph));
          downloadFile(JSON.stringify(rows, null, 2), `${filename}.json`, 'application/json');
        }
      }
    }
  }

  if (dataType.table && tableFormats.length > 0) {
    const exportUrl = injectDateParams(tableQueryUrl, selectedStartDate, selectedEndDate);
    const raw = await fetchData(exportUrl, tableCountUrl, undefined, 'actions');

    const filename = generateExportFilename(
      activeTenantName || 'Account',
      'Optimization_Actions_Table',
      filtersForName,
      today
    );

    if (tableFormats.includes('csv')) {
      const exportData = (raw as OptLogVolumeActions[]).map(optimizationActionToExportRowCsv);
      downloadFile(toCsv(exportData), `${filename}.csv`, 'text/csv;charset=utf-8;');
    }

    if (tableFormats.includes('json')) {
      const exportData = (raw as OptLogVolumeActions[]).map(optimizationActionToExportRowJson);
      downloadFile(JSON.stringify(exportData, null, 2), `${filename}.json`, 'application/json');
    }
  }
}
